import { createClerkSupabaseClient } from '../../lib/supabase'
import { migrateTemplate } from './templateMigrationService'
import type { 
  ChartConfig,
  Template as ChartTemplate,
  CreateTemplateRequest,
  TemplateAccessLevel,
  TemplateSearchFilters,
  TemplateSearchResponse,
  TemplateStatus,
  UpdateTemplateRequest
} from './schemas/templateSchema'
import type { Database, Json } from '../../interfaces/database.types'
import type { ServiceAuthProps } from '../../interfaces/serviceAuth'

type TemplateRow = Database['public']['Tables']['chart_templates']['Row']
type TemplateInsert = Database['public']['Tables']['chart_templates']['Insert']
type TemplateUpdate = Database['public']['Tables']['chart_templates']['Update']

/**
 * Convert database row to ChartTemplate interface with automatic migration
 */
function mapTemplateRowToTemplate(row: TemplateRow): ChartTemplate {
  // Prepare raw template data for migration
  const rawTemplate = {
    id: row.id,
    name: row.name,
    description: row.description || undefined,
    config: Array.isArray(row.config) ? row.config : [], // V2 format field name
    chartConfigs: Array.isArray(row.config) ? row.config : [], // V3 format field name
    version: row.version || 2, // Default to V2 if no version specified
    access_level: row.access_level,
    accessLevel: row.access_level,
    organization_id: row.organization_id,
    organizationId: row.organization_id,
    user_id: row.user_id,
    userId: row.user_id,
    status: row.status,
    created_at: row.created_at,
    createdAt: row.created_at ? new Date(row.created_at) : new Date(),
    updated_at: row.updated_at,
    updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),
    last_used_at: row.last_used_at,
    lastUsedAt: row.last_used_at ? new Date(row.last_used_at) : undefined
  }
  
  console.log(`TEMPLATE_LOAD: Loading template "${row.name}" (V${rawTemplate.version}) from database`)
  
  // Apply migration if needed
  const migrationResult = migrateTemplate(rawTemplate)
  
  if (migrationResult.success === false) {
    console.error(`TEMPLATE_ERROR: Migration failed for template "${row.name}":`, migrationResult.error)
    // Fallback: return basic template without migration for backward compatibility
    console.warn(`TEMPLATE_LOAD: Using fallback loading for template "${row.name}"`)
    return {
      id: row.id,
      name: row.name,
      description: row.description || undefined,
      chartConfigs: Array.isArray(row.config) ? row.config as unknown as ChartConfig[] : [],
      version: 6 as const,
      accessLevel: row.access_level as TemplateAccessLevel,
      organizationId: row.organization_id || undefined,
      userId: row.user_id,
      status: row.status as TemplateStatus,
      createdAt: new Date(row.created_at || new Date().toISOString()),
      updatedAt: new Date(row.updated_at || new Date().toISOString()),
      lastUsedAt: row.last_used_at ? new Date(row.last_used_at) : undefined
    }
  }
  
  if (migrationResult.migrationsApplied > 0) {
    console.log(`TEMPLATE_MIGRATION: Successfully migrated template "${row.name}" from V${rawTemplate.version} to V${migrationResult.version} (${migrationResult.migrationsApplied} migrations applied)`)
  } else {
    console.log(`TEMPLATE_LOAD: Template "${row.name}" already at current version V${migrationResult.version}`)
  }
  
  // Return the migrated template
  const migratedTemplate = migrationResult.template as ChartTemplate
  console.log(`TEMPLATE_LOAD: Successfully loaded template "${migratedTemplate.name}" (V${migratedTemplate.version})`)
  
  return migratedTemplate
}

/**
 * Convert CreateTemplateRequest to database insert format
 */
function mapCreateRequestToInsert(
  request: CreateTemplateRequest, 
  userId: string, 
  organizationId: string
): TemplateInsert {
  return {
    name: request.name,
    description: request.description,
    config: request.chartConfigs as Json, // JSONB conversion
    version: 6, // Current schema version (V6)
    access_level: request.accessLevel || 'org', // Default to org-wide
    organization_id: organizationId,
    user_id: userId,
    status: 'active',
    category: 'custom' // Explicitly set category
  }
}

/**
 * Simple TemplateService for managing chart templates
 * Designed for 10-15 templates with basic CRUD operations
 */
export class TemplateService {
  private sessionProps: ServiceAuthProps

  constructor(sessionProps: ServiceAuthProps) {
    this.sessionProps = sessionProps
  }

  /**
   * Create a new chart template
   */
  async createTemplate(request: CreateTemplateRequest): Promise<ChartTemplate> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    // Get user and organization IDs from session (JWT token sync ensures consistency)
    const userId = this.sessionProps.userId
    const organizationId = this.sessionProps.orgId

    // Convert to database format
    const templateData = mapCreateRequestToInsert(request, userId, organizationId)

    const { data, error } = await supabase
      .from('chart_templates')
      .insert(templateData)
      .select()
      .single()

    if (error) {
      console.error('TEMPLATE_ERROR: Supabase insert error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      })
      throw new Error(`Failed to create template: ${error.message}`)
    }

    console.log('✅ Template created successfully:', data.id)
    return mapTemplateRowToTemplate(data)
  }

  /**
   * Get template by ID
   */
  async getTemplate(id: string): Promise<ChartTemplate | null> {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    const { data, error } = await supabase
      .from('chart_templates')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') { // Row not found
        return null
      }
      throw new Error(`Failed to fetch template: ${error.message}`)
    }

    return mapTemplateRowToTemplate(data)
  }

  /**
   * Update an existing template
   */
  async updateTemplate(id: string, request: UpdateTemplateRequest): Promise<ChartTemplate> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    console.log('TEMPLATE_UPDATE: Starting template update process')
    console.log('TEMPLATE_UPDATE: Template ID:', id)
    console.log('TEMPLATE_UPDATE: Update request:', request)

    // Build update object
    const updateData: TemplateUpdate = {
      updated_at: new Date().toISOString()
    }

    if (request.name !== undefined) updateData.name = request.name
    if (request.description !== undefined) updateData.description = request.description
    if (request.chartConfigs !== undefined) updateData.config = request.chartConfigs as Json
    if (request.accessLevel !== undefined) updateData.access_level = request.accessLevel
    if (request.organizationId !== undefined) updateData.organization_id = request.organizationId
    if (request.status !== undefined) updateData.status = request.status
    // If saving chart configs, bump version to current
    if (request.chartConfigs !== undefined) {
      updateData.version = 6
    }
    
    console.log('TEMPLATE_SAVE: Update data to send:', updateData)
    if (updateData.config) {
      console.log('TEMPLATE_SAVE: Config yAxes being saved:', (updateData.config as ChartConfig[])[0]?.yAxes)
    }

    const { data, error } = await supabase
      .from('chart_templates')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('TEMPLATE_ERROR: Supabase update error:', error)
      throw new Error(`Failed to update template: ${error.message}`)
    }

    console.log('TEMPLATE_SAVE: Template updated in database:', data)
    console.log('TEMPLATE_SAVE: Saved yAxes config:', (data.config as ChartConfig[])[0]?.yAxes)
    return mapTemplateRowToTemplate(data)
  }

  /**
   * Delete a template
   */
  async deleteTemplate(id: string): Promise<boolean> {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    const { error } = await supabase
      .from('chart_templates')
      .delete()
      .eq('id', id)

    if (error) {
      throw new Error(`Failed to delete template: ${error.message}`)
    }

    return true
  }

  /**
   * Search templates (simplified for 10-15 templates)
   */
  async searchTemplates(filters: TemplateSearchFilters = {}): Promise<TemplateSearchResponse> {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    let query = supabase
      .from('chart_templates')
      .select('*', { count: 'exact' })

    // Apply basic filters
    if (filters.query) {
      query = query.or(`name.ilike.%${filters.query}%,description.ilike.%${filters.query}%`)
    }

    if (filters.accessLevels && filters.accessLevels.length > 0) {
      query = query.in('access_level', filters.accessLevels)
    }

    if (filters.statuses && filters.statuses.length > 0) {
      query = query.in('status', filters.statuses)
    }

    if (filters.organizationId) {
      query = query.eq('organization_id', filters.organizationId)
    }

    if (filters.userId) {
      query = query.eq('user_id', filters.userId)
    }

    // Simple sorting
    const sortBy = filters.sortBy || 'updatedAt'
    const sortDirection = filters.sortDirection || 'desc'
    
    // Map interface field names to database column names
    let dbSortField: string = sortBy
    if (sortBy === 'lastUsedAt') dbSortField = 'last_used_at'
    if (sortBy === 'updatedAt') dbSortField = 'updated_at'
    if (sortBy === 'createdAt') dbSortField = 'created_at'
    
    query = query.order(dbSortField, { ascending: sortDirection === 'asc' })

    const { data, error, count } = await query

    if (error) {
      throw new Error(`Failed to search templates: ${error.message}`)
    }

    const templates = (data || []).map(mapTemplateRowToTemplate)
    const totalCount = count || 0

    return {
      templates,
      totalCount
    }
  }

  /**
   * Get all templates accessible to current user
   */
  async getUserAccessibleTemplates(): Promise<TemplateSearchResponse> {
    return this.searchTemplates({
      userAccessibleOnly: true,
      sortBy: 'lastUsedAt',
      sortDirection: 'desc'
    })
  }

  /**
   * Clone a template
   */
  async cloneTemplate(
    sourceId: string, 
    newName: string, 
    accessLevel?: TemplateAccessLevel
  ): Promise<ChartTemplate> {
    const sourceTemplate = await this.getTemplate(sourceId)
    
    if (!sourceTemplate) {
      throw new Error('Source template not found')
    }

    const cloneRequest: CreateTemplateRequest = {
      name: newName,
      description: `Cloned from: ${sourceTemplate.name}`,
      chartConfigs: sourceTemplate.chartConfigs,
      accessLevel: accessLevel || 'org' // Default to org-wide
    }

    return this.createTemplate(cloneRequest)
  }


} 
